// src/pages/admin/AdminProjectsPage.js
import React, { useEffect, useMemo, useState } from 'react';
import pb from '../../lib/pocketbase';
import AdminLayout from '../../components/admin/AdminLayout';
import SpecificationsEditor from '../../components/admin/projects/SpecificationsEditor';

const statusOptions = [
    { value: 'draft', label: 'Brouillon' },
    { value: 'active', label: 'Actif' },
    { value: 'paused', label: 'En pause' },
    { value: 'done', label: 'Terminé' },
];

function AdminProjectsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ name: '', status: 'draft', start_date: '', end_date: '', preview_url: '', preview_label: '', preview_embed: false });
    const [editingSpecProject, setEditingSpecProject] = useState(null);

    const fetchProjects = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await pb.collection('projects').getList(1, 50, {
                sort: '-created',
            });
            setItems(res?.items ?? []);
        } catch (e) {
            setError(e?.message || 'Erreur au chargement des projets');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!form.name?.trim()) return;
        setCreating(true);
        setError('');
        try {
            const payload = {
                name: form.name.trim(),
                status: form.status,
            };
            if (form.start_date) payload.start_date = form.start_date;
            if (form.end_date) payload.end_date = form.end_date;
            if (form.preview_url) payload.preview_url = form.preview_url.trim();
            if (form.preview_label) payload.preview_label = form.preview_label.trim();
            if (form.preview_embed) payload.preview_embed = true;
            await pb.collection('projects').create(payload);
            setForm({ name: '', status: 'draft', start_date: '', end_date: '', preview_url: '', preview_label: '', preview_embed: false });
            await fetchProjects();
        } catch (e) {
            setError(e?.message || 'Erreur lors de la création');
        } finally {
            setCreating(false);
        }
    };

    const updateStatus = async (id, status) => {
        try {
            await pb.collection('projects').update(id, { status });
            setItems((prev) => prev.map((p) => (p.id === id ? { ...p, status } : p)));
        } catch (e) {
            alert(e?.message || 'Erreur lors de la mise à jour du statut');
        }
    };

    return (
        <AdminLayout title="Projets">
            <h1 style={{ marginTop: 0 }}>Projets</h1>

            <form onSubmit={handleCreate} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 12, alignItems: 'end', margin: '16px 0 24px' }}>
                <div>
                    <label>Nom</label>
                    <input type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Nom du projet" required />
                </div>
                <div>
                    <label>Statut</label>
                    <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                        {statusOptions.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label>Début</label>
                    <input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} />
                </div>
                <div>
                    <label>Fin</label>
                    <input type="date" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} />
                </div>
                <div>
                    <button type="submit" disabled={creating}>{creating ? 'Création...' : 'Créer'}</button>
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 12, alignItems: 'end' }}>
                    <div>
                        <label>URL de maquette</label>
                        <input type="url" value={form.preview_url} onChange={(e) => setForm(f => ({ ...f, preview_url: e.target.value }))} placeholder="https://..." />
                    </div>
                    <div>
                        <label>Libellé</label>
                        <input type="text" value={form.preview_label} onChange={(e) => setForm(f => ({ ...f, preview_label: e.target.value }))} placeholder="Ex: Maquette Figma" />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 2 }}>
                        <label htmlFor="preview_embed" style={{ margin: 0 }}>Aperçu intégré</label>
                        <input id="preview_embed" type="checkbox" checked={form.preview_embed} onChange={(e) => setForm(f => ({ ...f, preview_embed: e.target.checked }))} />
                    </div>
                </div>
            </form>

            {error && (
                <div style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</div>
            )}

            {loading ? (
                <div>Chargement...</div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: 8 }}>Nom</th>
                                <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: 8 }}>Statut</th>
                                <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: 8 }}>Début</th>
                                <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: 8 }}>Fin</th>
                                <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: 8 }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((p) => (
                                <tr key={p.id}>
                                    <td style={{ borderBottom: '1px solid #f3f4f6', padding: 8 }}>{p.name}</td>
                                    <td style={{ borderBottom: '1px solid #f3f4f6', padding: 8 }}>
                                        <select value={p.status} onChange={(e) => updateStatus(p.id, e.target.value)}>
                                            {statusOptions.map((o) => (
                                                <option key={o.value} value={o.value}>{o.label}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td style={{ borderBottom: '1px solid #f3f4f6', padding: 8 }}>{p.start_date ? new Date(p.start_date).toLocaleDateString() : '-'}</td>
                                    <td style={{ borderBottom: '1px solid #f3f4f6', padding: 8 }}>{p.end_date ? new Date(p.end_date).toLocaleDateString() : '-'}</td>
                                    <td style={{ borderBottom: '1px solid #f3f4f6', padding: 8 }}>
                                        <button
                                            onClick={() => setEditingSpecProject(p.id)}
                                            style={{
                                                fontSize: '0.85rem', padding: '6px 12px', background: '#f0f9ff', color: '#0369a1',
                                                border: '1px solid #bae6fd', borderRadius: 6, cursor: 'pointer', fontWeight: 500
                                            }}
                                        >
                                            Cahier des Charges
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editingSpecProject && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
                }}>
                    <div style={{
                        background: 'white', width: '90%', maxWidth: 1100, height: '85vh', maxHeight: '85vh', borderRadius: 12, padding: 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', overflow: 'hidden'
                    }}>
                        <SpecificationsEditor
                            projectId={editingSpecProject}
                            onClose={() => setEditingSpecProject(null)}
                        />
                    </div>
                </div>
            )}

        </AdminLayout>
    );
}

export default AdminProjectsPage;
